import { createContext, useContext, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/App";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({
    pillars: [],
    protocols: [],
    toolkits: [],
    dataGuides: [],
    training: [],
    assets: [],
    contacts: [],
  });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAllData = useCallback(async () => {
    try {
      const [
        statsRes,
        pillarsRes,
        protocolsRes,
        toolkitsRes,
        guidesRes,
        trainingRes,
        assetsRes,
        contactsRes,
        storageRes,
        emailRes,
      ] = await Promise.all([
        api.get("/analytics/stats"),
        api.get("/pillars"),
        api.get("/protocols"),
        api.get("/toolkits"),
        api.get("/data-guides"),
        api.get("/training"),
        api.get("/assets"),
        api.get("/contacts"),
        api.get("/storage-info"),
        api.get("/email-info"),
      ]);

      setStats({...statsRes.data, storage: storageRes.data, email: emailRes.data});
      setData({
        pillars: pillarsRes.data,
        protocols: protocolsRes.data,
        toolkits: toolkitsRes.data,
        dataGuides: guidesRes.data,
        training: trainingRes.data,
        assets: assetsRes.data,
        contacts: contactsRes.data,
      });
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const getDefaultFormData = useCallback((type) => {
    switch (type) {
      case "pillars":
        return {
          title: "",
          slug: "",
          description: "",
          purpose: "",
          non_negotiables: [],
          failure_points: [],
          recommended_cadence: "",
          icon: "Shield",
          color: "#F25F4C",
          order: 0,
        };
      case "protocols":
        return {
          title: "",
          slug: "",
          summary: "",
          pillar_id: "",
          situation_tags: [],
          urgency: "routine",
          applicable_settings: ["all"],
          applicable_groups: ["All"],
          triggers: "",
          roles: [],
          steps: [],
          required_documentation: [],
          data_to_review: [],
          implementation_notes: "",
          compliance_notes: "",
          asset_ids: [],
        };
      case "toolkits":
        return {
          title: "",
          slug: "",
          summary: "",
          protocol_ids: [],
          asset_ids: [],
          icon: "Package",
          color: "#10B981",
        };
      case "data-guides":
        return {
          title: "",
          slug: "",
          domain: "",
          what_to_pull: [],
          how_to_interpret: "",
          common_patterns: [],
          first_90_day_actions: [],
          icon: "BarChart3",
          color: "#3B82F6",
        };
      case "training":
        return {
          title: "",
          slug: "",
          objective: "",
          time_estimate: "",
          video_url: "",
          slide_url: "",
          pillar_id: "",
          order: 0,
        };
      case "assets":
        return {
          title: "",
          description: "",
          file_url: "",
          file_type: "pdf",
          tags: [],
          version: "1.0",
        };
      default:
        return {};
    }
  }, []);

  const openCreateModal = useCallback((type) => {
    setEditingItem({ type, isNew: true });
    setFormData(getDefaultFormData(type));
    setModalOpen(true);
  }, [getDefaultFormData]);

  const openEditModal = useCallback((type, item) => {
    setEditingItem({ type, isNew: false, item });
    setFormData(item);
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      const { type, isNew, item } = editingItem;
      const endpoints = {
        pillars: "/pillars",
        protocols: "/protocols",
        toolkits: "/toolkits",
        "data-guides": "/data-guides",
        training: "/training",
        assets: "/assets",
      };
      
      const endpoint = endpoints[type];
      if (!endpoint) throw new Error("Unknown type");

      if (isNew) {
        await api.post(endpoint, formData);
        toast.success("Created successfully");
      } else {
        await api.put(`${endpoint}/${item.id}`, formData);
        toast.success("Updated successfully");
      }

      setModalOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save");
    }
  }, [editingItem, formData, fetchAllData]);

  const handleDelete = useCallback(async (type, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const endpoints = {
        pillars: `/pillars/${id}`,
        protocols: `/protocols/${id}`,
        toolkits: `/toolkits/${id}`,
        "data-guides": `/data-guides/${id}`,
        training: `/training/${id}`,
        assets: `/assets/${id}`,
      };
      
      const endpoint = endpoints[type];
      if (!endpoint) throw new Error("Unknown type");

      await api.delete(endpoint);
      toast.success("Deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  }, [fetchAllData]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      const { file_url, file_type, original_filename } = response.data;
      
      setFormData((prev) => ({
        ...prev,
        file_url: file_url,
        file_type: file_type,
        title: prev.title || original_filename.replace(/\.[^/.]+$/, ""),
      }));
      
      toast.success(`File uploaded: ${original_filename}`);
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, []);

  const value = {
    stats,
    data,
    loading,
    modalOpen,
    setModalOpen,
    editingItem,
    formData,
    setFormData,
    uploading,
    fileInputRef,
    fetchAllData,
    openCreateModal,
    openEditModal,
    handleSave,
    handleDelete,
    handleFileUpload,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
}
